/**
 * Serial capture queue — one creative artifact export at a time
 * to avoid spawning many concurrent WebGL contexts (portfolio).
 *
 * Always goes through `exportCreativeArtifact` (canonical production primitive).
 */
import {
  deserializeEditGraph,
  resolveEdit,
  validateEditGraphAgainstPack,
  type SourcePack,
} from "@desk/core";
import { shouldShowReconstructionLabel } from "../../game/disclosure.js";
import {
  artifactFingerprint,
  getCachedArtifactUrl,
  putCachedArtifactUrl,
} from "./artifact-cache.js";
import { exportCreativeArtifact } from "./creative-artifact-export.js";
import { notePerfCaptureQueue } from "../../perf/perf-capture-lifecycle.js";

export interface CaptureArtifactRequest {
  packId: string;
  pack: SourcePack;
  graphJson: string;
  width?: number;
  height?: number;
  scoreText?: string;
  /** Cosmetic only — never fed into scoring. */
  resultCardTheme?: "default" | "portfolio-alt";
  /** Exact edit tick when provided (smoke / targeted capture). */
  tick?: number;
}

/** Fully resolved render params — shared by capture export and cache fingerprint. */
export interface ResolvedArtifactCaptureInput {
  packId: string;
  packVersion: string;
  graphJson: string;
  width: number;
  height: number;
  scoreText: string;
  resultCardTheme: "default" | "portfolio-alt";
}

export interface ResolveArtifactCaptureOptions {
  packId: string;
  packVersion: string;
  graphJson: string;
  width?: number;
  height?: number;
  scoreText?: string;
  resultCardTheme?: "default" | "portfolio-alt";
  /** When true and dims omitted, use compact preview sizes (180×320). */
  compact?: boolean;
}

/**
 * Single source of truth for effective capture width/height/score/theme.
 * Preview and capture must fingerprint this exact object form.
 */
export function resolveArtifactCaptureInput(
  options: ResolveArtifactCaptureOptions,
): ResolvedArtifactCaptureInput {
  const compact = options.compact === true;
  return {
    packId: options.packId,
    packVersion: options.packVersion,
    graphJson: options.graphJson,
    width: options.width ?? (compact ? 180 : 270),
    height: options.height ?? (compact ? 320 : 480),
    scoreText: options.scoreText ?? "Reach –",
    resultCardTheme: options.resultCardTheme ?? "default",
  };
}

export interface CaptureArtifactResult {
  ok: true;
  objectUrl: string;
  fingerprint: string;
  fromCache: boolean;
  showReconstructionLabel: boolean;
  mimeType: string;
}

export interface CaptureArtifactError {
  ok: false;
  fingerprint: string;
  reason: string;
}

type CaptureOutcome = CaptureArtifactResult | CaptureArtifactError;

let queue: Promise<unknown> = Promise.resolve();
let activeCaptures = 0;
let maxActiveCaptures = 0;

/** Live queue instrumentation for smokes / unit tests. */
export function getArtifactCaptureQueueStats(): {
  active: number;
  maxActive: number;
} {
  return { active: activeCaptures, maxActive: maxActiveCaptures };
}

/** Test-only reset of peak concurrency counter. */
export function resetArtifactCaptureQueueStatsForTests(): void {
  maxActiveCaptures = 0;
}

export function enqueueArtifactCapture(
  request: CaptureArtifactRequest,
): Promise<CaptureOutcome> {
  const run = async () => {
    activeCaptures += 1;
    maxActiveCaptures = Math.max(maxActiveCaptures, activeCaptures);
    notePerfCaptureQueue(activeCaptures, maxActiveCaptures);
    try {
      return await captureArtifactOnce(request);
    } finally {
      activeCaptures = Math.max(0, activeCaptures - 1);
      notePerfCaptureQueue(activeCaptures, maxActiveCaptures);
    }
  };
  const next = queue.then(run, run);
  queue = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

async function captureArtifactOnce(
  request: CaptureArtifactRequest,
): Promise<CaptureOutcome> {
  const captureInput = resolveArtifactCaptureInput({
    packId: request.packId,
    packVersion: request.pack.version,
    graphJson: request.graphJson,
    width: request.width,
    height: request.height,
    scoreText: request.scoreText,
    resultCardTheme: request.resultCardTheme,
  });
  const fingerprint = artifactFingerprint(captureInput);
  let showLabel = false;
  try {
    const graph = deserializeEditGraph(request.graphJson);
    validateEditGraphAgainstPack(graph, request.pack);
    const resolved = resolveEdit(graph, request.pack);
    showLabel = shouldShowReconstructionLabel(request.pack, resolved);
    const cached = getCachedArtifactUrl(fingerprint);
    if (cached && request.tick === undefined) {
      return {
        ok: true,
        objectUrl: cached,
        fingerprint,
        fromCache: true,
        showReconstructionLabel: showLabel,
        mimeType: "image/png",
      };
    }

    const exported = await exportCreativeArtifact({
      pack: request.pack,
      editGraph: graph,
      resolved,
      width: captureInput.width,
      height: captureInput.height,
      tick: request.tick,
      scoreText: captureInput.scoreText,
      resultCardTheme: captureInput.resultCardTheme,
      outputTickRate: request.pack.tickRate,
    });
    if (!exported.ok) {
      return { ok: false, fingerprint, reason: exported.reason };
    }
    const blob = new Blob([new Uint8Array(exported.data)], {
      type: exported.mimeType,
    });
    const objectUrl = URL.createObjectURL(blob);
    if (request.tick === undefined) {
      putCachedArtifactUrl(fingerprint, objectUrl);
    }
    return {
      ok: true,
      objectUrl,
      fingerprint,
      fromCache: false,
      showReconstructionLabel: exported.showReconstructionLabel,
      mimeType: exported.mimeType,
    };
  } catch (err) {
    return {
      ok: false,
      fingerprint,
      reason: err instanceof Error ? err.message : "capture failed",
    };
  }
}
